import React, { useState } from 'react';
import { DatePicker, Select } from 'antd';
import { ToastError } from 'components/Toaster';
import { BecoMovieService } from 'core/http/service/BecoMovie.service';
import dayjs from 'dayjs';
import Colors from 'enums/Colors';
import TipoAcessibilidade from 'models/becoMovie/TipoAcessibilidade';
import { ButtonHomeV2 } from 'pages/home/components/homeV2/button/ButtonHomeV2.styled';
import { IoReload } from 'react-icons/io5';
import styled from 'styled-components';

const Styled = {
    Container: styled.div`
        display: flex;
        flex-direction: column;
        max-width: 1100px;
        min-height: 320px;
        background-color: #ffffff;
        padding: 40px;
        gap: 2rem;
        margin-bottom: 200px;

        -webkit-box-shadow: 15px 15px 5px -11px rgba(0, 0, 0, 0.27);
        -moz-box-shadow: 15px 15px 5px -11px rgba(0, 0, 0, 0.27);
        box-shadow: 15px 15px 5px -11px rgba(0, 0, 0, 0.27);
    `,

    Header: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,

    Title: styled.h1`
        font-size: 26px;
        font-weight: 700;
        color: ${Colors.Blue};
        text-decoration: underline;
        text-underline-offset: calc(1rem);
    `,

    ClearFilter: styled.div`
        display: flex;
        gap: 0.5rem;
        align-items: center;
        cursor: pointer;
    `,

    Content: styled.div`
        display: flex;
        flex-wrap: wrap;
        gap: 2rem;
        margin-top: 30px;
    `,

    SubTitle: styled.p`
        font-size: 14px;
        font-weight: 600;
        color: ${Colors.Blue};
        margin-bottom: 10px;
    `,
};

const TIPO_ACESSIBILIDADE_OPTIONS = [
    { value: TipoAcessibilidade.ORIGINAL, label: 'Original' },
    { value: TipoAcessibilidade.LIBRAS, label: 'Em Libras' },
    { value: TipoAcessibilidade.AUDIODESCRICAO, label: 'Com audiodescrição' },
];

const CardReportFilmeSenna = () => {
    const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
    const [dataPeriodo, setDataPeriodo] = useState<string[]>([]);
    // undefined means every accessibility version.
    const [tipoAcessibilidade, setTipoAcessibilidade] = useState<TipoAcessibilidade | undefined>(undefined);

    const becoMovieService = new BecoMovieService();

    const clearFilter = () => {
        setDataPeriodo([]);
        setTipoAcessibilidade(undefined);
    };

    const handleRelatorioFilmeSenna = async () => {
        if (dataPeriodo.length !== 2 || !dataPeriodo[0] || !dataPeriodo[1]) {
            return ToastError('Selecione o período do relatório!');
        }

        setLoadingSubmit(true);

        try {
            const [dateInicio, dateFim] = dataPeriodo;
            const response = await becoMovieService.getRelatorioAcessos({
                dateInicio,
                dateFim,
                ...(tipoAcessibilidade && { tipoAcessibilidade }),
            });

            if (response?.status !== 200) {
                throw new Error(`status ${response?.status}`);
            }

            const blob = new Blob([response.data], { type: 'text/csv;charset=UTF-8' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `relatorio-acessos-filme-senna-${tipoAcessibilidade ?? 'todos'}-${dateInicio}-a-${dateFim}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            ToastError('Ocorreu algum erro no servidor!');
            console.log('🚀 ~ handleRelatorioFilmeSenna ~ error:', error);
        }

        setLoadingSubmit(false);
    };

    return (
        <Styled.Container>
            <Styled.Header>
                <Styled.Title>ACESSOS - FILME SENNA</Styled.Title>
                <Styled.ClearFilter onClick={clearFilter}>
                    <IoReload size={24} />
                    Limpar filtro
                </Styled.ClearFilter>
            </Styled.Header>
            <Styled.Content>
                <div>
                    <Styled.SubTitle>Tipo de acessibilidade</Styled.SubTitle>
                    <Select
                        size="large"
                        style={{ width: '240px', height: '50px' }}
                        placeholder="Todos"
                        allowClear
                        value={tipoAcessibilidade}
                        onChange={(value?: TipoAcessibilidade) => setTipoAcessibilidade(value)}
                        options={TIPO_ACESSIBILIDADE_OPTIONS}
                    />
                </div>
                <div>
                    <Styled.SubTitle>Período de acesso</Styled.SubTitle>
                    <DatePicker.RangePicker
                        size="large"
                        style={{ height: 50 }}
                        format="YYYY-MM-DD"
                        value={dataPeriodo.length === 2 && dataPeriodo[0] ? [dayjs(dataPeriodo[0]), dayjs(dataPeriodo[1])] : null}
                        placeholder={['Início', 'Término']}
                        allowEmpty={[false, false]}
                        onChange={(_, dateString) => setDataPeriodo(dateString)}
                    />
                </div>
            </Styled.Content>
            <ButtonHomeV2
                disabled={loadingSubmit}
                style={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={() => {
                    !loadingSubmit && handleRelatorioFilmeSenna();
                }}
            >
                {loadingSubmit ? <div className="loaderCreater" /> : 'Gerar relatório'}
            </ButtonHomeV2>
        </Styled.Container>
    );
};

export default CardReportFilmeSenna;
